import React, { useCallback, useId } from 'react';

/**
 * 탭 목록(WAI-ARIA tabs 패턴)용 소형 훅.
 * - 활성 탭만 Tab 순서에 들어가고(tabIndex 0), 나머지는 -1
 * - 좌/우 화살표·Home/End로 이동하면서 즉시 활성화(자동 활성화 패턴)
 * - role/aria-selected/aria-controls/id를 한 벌로 묶어 준다
 *
 * 사용:
 *   const tabs = useRovingTabs(TAB_KEYS, activeTab, setActiveTab);
 *   <div {...tabs.tablistProps}> {keys.map(k => <button {...tabs.tabProps(k)}>…</button>)} </div>
 *   <div {...tabs.panelProps(activeTab)}>…</div>
 */
export function useRovingTabs<K extends string>(
  keys: readonly K[],
  active: K,
  setActive: (key: K) => void,
  orientation: 'horizontal' | 'vertical' = 'horizontal'
) {
  const base = useId();
  const tabId = (k: K) => `${base}-tab-${k}`;
  const panelId = (k: K) => `${base}-panel-${k}`;

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
    const prevKey = orientation === 'horizontal' ? 'ArrowLeft' : 'ArrowUp';
    const nextKey = orientation === 'horizontal' ? 'ArrowRight' : 'ArrowDown';
    if (![prevKey, nextKey, 'Home', 'End'].includes(e.key)) return;
    const idx = keys.indexOf(active);
    let next = idx;
    if (e.key === nextKey) next = (idx + 1) % keys.length;
    else if (e.key === prevKey) next = (idx - 1 + keys.length) % keys.length;
    else if (e.key === 'Home') next = 0;
    else next = keys.length - 1;
    e.preventDefault();
    const key = keys[next];
    setActive(key);
    const el = e.currentTarget.querySelector<HTMLElement>(`#${CSS.escape(tabId(key))}`);
    el?.focus();
  }, [keys, active, setActive, orientation, base]);

  return {
    tablistProps: { role: 'tablist' as const, 'aria-orientation': orientation, onKeyDown },
    tabProps: (k: K) => ({
      role: 'tab' as const,
      id: tabId(k),
      'aria-selected': active === k,
      'aria-controls': panelId(k),
      tabIndex: active === k ? 0 : -1,
    }),
    panelProps: (k: K) => ({
      role: 'tabpanel' as const,
      id: panelId(k),
      'aria-labelledby': tabId(k),
    }),
  };
}
